/**
 * useDevAgentSession — all session management logic extracted from DevAgent.tsx.
 *
 * Owns: sessions list, MCP tools, session CRUD, rename, menus, multi-select,
 * SSE connection, keyboard shortcuts, session init, history/artifact loading,
 * session state restoration, "Improve with AI" intent, handleSend, handleApprove,
 * and mount-time setup (load tools, clean pipeline steps).
 */
import { useEffect, useCallback, useState, useRef } from "react";
import { useShallow } from "zustand/react/shallow";
import { useAuth } from "@/hooks/auth/useAuth";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useDevAgentStore } from "@/stores/devAgentStore";
import {
  approveDevAgentAction,
  createDevAgentSession,
  renameDevAgentSession,
  deleteDevAgentSession,
  listMCPTools,
  type MCPTool,
} from "./devAgentService";
import { restoreDevAgentSessionState } from "./devAgentSession/restoreSessionState";
import {
  hydrateDevAgentFeedFromHistory,
  loadDevAgentPinnedArtifacts,
  loadBackendArtifacts,
} from "./devAgentSession/hydrateSessionHistory";
import {
  applyDevAgentSessionSwitch,
  fetchDevAgentSessions,
  loadInitialDevAgentSessions,
  resolveDevAgentSessionTicket,
} from "./devAgentSession/sessionLifecycle";
import {
  persistLastDevAgentSession,
} from "./devAgentSession/sessionPersistence";
import { runDevAgentSend } from "./devAgentSession/runDevAgentSend";
import { useConsumeImproveWithAIIntent } from "./devAgentSession/useConsumeImproveWithAIIntent";
import { useDevAgentShortcuts } from "./devAgentSession/useDevAgentShortcuts";
import {
  type DevAgentChatSessionRow,
} from "./devAgentSession/types";
import { useDevAgentSSE } from "./useCopilotBridge";
import toast from "react-hot-toast";
import type { useMetadataEditorState } from "./components/useMetadataEditorState";

// ---------------------------------------------------------------------------
// Hook options & return types
// ---------------------------------------------------------------------------

export interface UseDevAgentSessionOpts {
  /** Setter for the left sidebar collapsed state (passed from DevAgent layout). */
  setSidebarCollapsed: (fn: (v: boolean) => boolean) => void;
  /** Editor state hook instance (openTab, openFiles, setAcceptedCode, etc.). */
  editor: ReturnType<typeof useMetadataEditorState>;
  /** Callback to open a file in the editor panel. */
  handleOpenFileInEditor: (file: { id: string; name: string; metadataType?: string }) => void;
}

export interface UseDevAgentSessionReturn {
  // Sessions list
  sessions: DevAgentChatSessionRow[];
  setSessions: React.Dispatch<React.SetStateAction<DevAgentChatSessionRow[]>>;
  refreshSessions: () => void;

  // MCP tools
  mcpTools: MCPTool[];
  setMcpTools: React.Dispatch<React.SetStateAction<MCPTool[]>>;

  // Session CRUD
  handleNewSession: () => Promise<void>;
  switchSession: (uuid: string) => void | Promise<void>;
  /** Re-open SSE for the current session (header Retry) — no full page reload. */
  reconnectSSE: () => void | Promise<void>;
  handleDeleteSession: (uuid: string) => Promise<void>;
  handleMultiDelete: () => Promise<void>;

  // Rename
  editingSessionId: string | null;
  editingTitle: string;
  setEditingTitle: React.Dispatch<React.SetStateAction<string>>;
  startRename: (uuid: string, currentTitle: string) => void;
  commitRename: (uuid: string) => Promise<void>;

  // Menu state
  menuOpenId: string | null;
  setMenuOpenId: React.Dispatch<React.SetStateAction<string | null>>;
  confirmDeleteId: string | null;
  setConfirmDeleteId: React.Dispatch<React.SetStateAction<string | null>>;

  // Multi-select
  selectedForDelete: Set<string>;
  setSelectedForDelete: React.Dispatch<React.SetStateAction<Set<string>>>;
  multiSelectMode: boolean;
  setMultiSelectMode: React.Dispatch<React.SetStateAction<boolean>>;
  toggleSelectForDelete: (uuid: string) => void;

  // SSE
  handleStop: () => void;
  connectSSE: (uuid: string) => void;

  // Callbacks for feed / approval
  handleSend: (message: string, displayText?: string) => Promise<void>;
  handleApprove: (approved: boolean) => Promise<void>;

  // Convenience: current session object (may be undefined)
  currentSession: DevAgentChatSessionRow | undefined;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useDevAgentSession(opts: UseDevAgentSessionOpts): UseDevAgentSessionReturn {
  const { setSidebarCollapsed, editor, handleOpenFileInEditor } = opts;

  const { user } = useAuth();
  const orgId = useAuthStoreV1((s) => s.selectedOrg?.id);

  const { sessionUuid, reset, closeDrawer, runStatus } = useDevAgentStore(
    useShallow((s) => ({
      sessionUuid: s.sessionUuid,
      reset: s.reset,
      closeDrawer: s.closeDrawer,
      runStatus: s.runStatus,
    }))
  );

  // Remember active session for reload (Zustand resets to null on refresh).
  useEffect(() => {
    persistLastDevAgentSession(user?.id, orgId, sessionUuid);
  }, [sessionUuid, user?.id, orgId]);

  // ── 1. Sessions list ──────────────────────────────────────────────────
  const [sessions, setSessions] = useState<DevAgentChatSessionRow[]>([]);

  const refreshSessions = useCallback(() => {
    if (!user?.id) return;
    fetchDevAgentSessions(user.id, orgId)
      .then(setSessions)
      .catch(() => {});
  }, [user?.id, orgId]);

  // ── 2. MCP tools ─────────────────────────────────────────────────────
  const [mcpTools, setMcpTools] = useState<MCPTool[]>([]);

  // ── 5. Menu state ─────────────────────────────────────────────────────
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // ── 4. Rename logic ───────────────────────────────────────────────────
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");

  const startRename = useCallback((uuid: string, currentTitle: string) => {
    setEditingSessionId(uuid);
    setEditingTitle(currentTitle);
    setMenuOpenId(null);
  }, []);

  const commitRename = useCallback(
    async (uuid: string) => {
      const trimmed = editingTitle.trim();
      if (!trimmed) {
        setEditingSessionId(null);
        return;
      }
      try {
        await renameDevAgentSession(uuid, trimmed);
        setSessions((prev) =>
          prev.map((s) => (s.uuid === uuid ? { ...s, title: trimmed } : s))
        );
      } catch (e: any) {
        toast.error(e?.message || "Failed to rename session");
      }
      setEditingSessionId(null);
    },
    [editingTitle]
  );

  // ── 6. Multi-select ──────────────────────────────────────────────────
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [multiSelectMode, setMultiSelectMode] = useState(false);

  const toggleSelectForDelete = useCallback((uuid: string) => {
    setSelectedForDelete((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  }, []);

  // ── 7. SSE connection ────────────────────────────────────────────────
  const { stop: handleStop, disconnect: handleDisconnect, connectTo: connectSSE, forceReconnect } =
    useDevAgentSSE(sessionUuid);

  const reconnectSSE = useCallback(async () => {
    const u = useDevAgentStore.getState().sessionUuid;
    if (!u) return;
    const ticket = await resolveDevAgentSessionTicket({
      sessionUuid: u,
      sessions,
      userId: user?.id,
      orgId,
      setSessions,
    });
    useDevAgentStore.getState().setSseTicket(ticket);
    forceReconnect(u);
  }, [forceReconnect, sessions, user?.id, orgId]);

  useEffect(() => {
    const handleAuthRecovered = async () => {
      refreshSessions();
      const currentUuid = useDevAgentStore.getState().sessionUuid;
      if (!currentUuid) return;
      const ticket = await resolveDevAgentSessionTicket({
        sessionUuid: currentUuid,
        sessions: useDevAgentStore.getState().sessionUuid === currentUuid ? sessions : [],
        userId: user?.id,
        orgId,
        setSessions,
      });
      if (ticket) useDevAgentStore.getState().setSseTicket(ticket);
      await restoreDevAgentSessionState(currentUuid).catch(() => {});
      forceReconnect(currentUuid);
    };
    window.addEventListener("ncoder:auth-recovered", handleAuthRecovered);
    return () => window.removeEventListener("ncoder:auth-recovered", handleAuthRecovered);
  }, [forceReconnect, orgId, refreshSessions, sessions, user?.id]);

  useDevAgentShortcuts({ runStatus, handleStop, setSidebarCollapsed });

  // ── 17. Clean stale pipeline steps on mount ──────────────────────────
  useEffect(() => {
    useDevAgentStore.getState().clearPipelineSteps();
  }, []);

  // ── 16. Load MCP tools + sessions on mount ───────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    listMCPTools(user.id).then(setMcpTools).catch(() => {});
    loadInitialDevAgentSessions({
      userId: user.id,
      orgId,
      currentSessionUuid: useDevAgentStore.getState().sessionUuid,
    })
      .then(({ sessions: loadedSessions, selectedSessionUuid, sseTicket }) => {
        setSessions(loadedSessions);
        if (!useDevAgentStore.getState().sessionUuid && selectedSessionUuid) {
          applyDevAgentSessionSwitch(selectedSessionUuid, sseTicket);
        }
      })
      .catch(() => {});
  }, [user?.id, orgId]);

  useConsumeImproveWithAIIntent({ handleOpenFileInEditor });

  // ── 10–12. History + backend artifacts + session state on session change ──
  useEffect(() => {
    if (!sessionUuid) return;
    const ac = new AbortController();
    const { signal } = ac;
    // Load backend artifacts BEFORE painting history so `[ARTIFACT: art_xxx]`
    // markers in persisted messages can be rewritten to code blocks on first
    // render. Without this sequence, users briefly see raw marker text.
    void (async () => {
      await loadBackendArtifacts(sessionUuid, signal);
      if (signal.aborted) return;
      await hydrateDevAgentFeedFromHistory(sessionUuid, signal).catch(() => {});
      if (signal.aborted) return;
      await restoreDevAgentSessionState(sessionUuid).catch(() => {});
    })();
    void loadDevAgentPinnedArtifacts(sessionUuid, signal);
    return () => ac.abort();
  }, [sessionUuid]);

  // ── 14. handleSend ───────────────────────────────────────────────────
  const sendingRef = useRef(false);
  const handleSend = useCallback(
    async (message: string, displayText?: string) => {
      await runDevAgentSend(message, displayText, {
        userId: user?.id,
        orgId,
        sessionUuid,
        setSessionUuid: useDevAgentStore.getState().setSessionUuid,
        connectSSE,
        forceReconnect,
        refreshSessions,
        editor,
        sendingRef,
      });
    },
    [
      user?.id,
      orgId,
      sessionUuid,
      connectSSE,
      forceReconnect,
      refreshSessions,
      editor,
    ]
  );

  // ── 15. handleApprove ────────────────────────────────────────────────
  const handleApprove = useCallback(
    async (approved: boolean) => {
      if (!sessionUuid) return;
      const pending = useDevAgentStore.getState().pendingApproval;
      if (!pending) return;
      try {
        await approveDevAgentAction(sessionUuid, {
          approved,
          approval_id: pending.approvalId,
        });
        useDevAgentStore.getState().setPendingApproval(null);
        closeDrawer();
        toast.success(approved ? "Approved" : "Rejected");
      } catch (e: any) {
        toast.error(e?.message || "Approval failed");
      }
    },
    [sessionUuid, closeDrawer]
  );

  // ── 3. Session CRUD ──────────────────────────────────────────────────
  const handleNewSession = useCallback(async () => {
    // Eagerly create a fresh session in the backend so the UI lands on a real
    // empty session — not a null one that gets re-resolved to "the latest"
    // by the auto-select effect on the next render.
    if (!user?.id) return;
    reset();
    try {
      const created = await createDevAgentSession(user.id, orgId);
      applyDevAgentSessionSwitch(created.uuid, (created as any).sse_ticket ?? null);
      refreshSessions();
    } catch (err) {
      console.warn("[handleNewSession] create failed:", err);
    }
  }, [reset, refreshSessions, user?.id, orgId]);

  const switchSession = useCallback(
    async (uuid: string) => {
      const current = useDevAgentStore.getState().sessionUuid;
      if (uuid === current) return;
      handleDisconnect();
      const ticket = await resolveDevAgentSessionTicket({
        sessionUuid: uuid,
        sessions,
        userId: user?.id,
        orgId,
        setSessions,
      });
      applyDevAgentSessionSwitch(uuid, ticket);
    },
    [handleDisconnect, sessions, user?.id, orgId],
  );

  const handleDeleteSession = useCallback(
    async (uuid: string) => {
      try {
        await deleteDevAgentSession(uuid);
        setSessions((prev) => prev.filter((s) => s.uuid !== uuid));
        if (sessionUuid === uuid) {
          reset();
        }
        toast.success("Session deleted");
      } catch (e: any) {
        toast.error(e?.message || "Failed to delete session");
      }
      setConfirmDeleteId(null);
      setMenuOpenId(null);
    },
    [sessionUuid, reset]
  );

  const handleMultiDelete = useCallback(async () => {
    const uuids = Array.from(selectedForDelete);
    if (uuids.length === 0) return;
    try {
      await Promise.all(uuids.map((uuid) => deleteDevAgentSession(uuid)));
      setSessions((prev) => prev.filter((s) => !selectedForDelete.has(s.uuid)));
      if (sessionUuid && selectedForDelete.has(sessionUuid)) {
        reset();
      }
      toast.success(`${uuids.length} session(s) deleted`);
    } catch (e: any) {
      toast.error(e?.message || "Failed to delete sessions");
    }
    setSelectedForDelete(new Set());
    setMultiSelectMode(false);
  }, [selectedForDelete, sessionUuid, reset]);

  // ── Close menus on outside click ─────────────────────────────────────
  useEffect(() => {
    if (!menuOpenId && !confirmDeleteId) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-session-menu]")) {
        setMenuOpenId(null);
        setConfirmDeleteId(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpenId, confirmDeleteId]);

  // ── Convenience: current session object ──────────────────────────────
  const currentSession = sessions.find((s) => s.uuid === sessionUuid);

  // ── Return ───────────────────────────────────────────────────────────
  return {
    // Sessions list
    sessions,
    setSessions,
    refreshSessions,

    // MCP tools
    mcpTools,
    setMcpTools,

    // Session CRUD
    handleNewSession,
    switchSession,
    handleDeleteSession,
    handleMultiDelete,

    // Rename
    editingSessionId,
    editingTitle,
    setEditingTitle,
    startRename,
    commitRename,

    // Menu state
    menuOpenId,
    setMenuOpenId,
    confirmDeleteId,
    setConfirmDeleteId,

    // Multi-select
    selectedForDelete,
    setSelectedForDelete,
    multiSelectMode,
    setMultiSelectMode,
    toggleSelectForDelete,

    // SSE
    handleStop,
    connectSSE,
    reconnectSSE,

    // Callbacks
    handleSend,
    handleApprove,

    // Convenience
    currentSession,
  };
}
